import DesktopCourseDetails from 'components/CourseSettings/CourseDetails/DesktopCourseDetails';
import MobileCourseDetails from 'components/CourseSettings/CourseDetails/MobileCourseDetails';
import TabletCourseDetails from 'components/CourseSettings/CourseDetails/TabletCourseDetails';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import courseService from 'services/course';
import useFormValidation from 'services/hooks/useFormValidation';
import { Loading } from 'shell/components';

const CourseDetails = ({ setSelected, setAlertDialog }) => {
    const { course, updateCourse } = useApp();
    const { auth } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const { message } = useSnackbar();
    const { device } = useViewport();
    const { t } = useTranslation();

    const [form, setForm] = useState({
        name: '',
        language: null,
    });
    const [isDirty, setIsDirty] = useState(false);
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const getErrorMessage = useCallback(
        (key, value) => {
            if (['name'].includes(key)) value = value?.trim();
            switch (key) {
                case 'name':
                    return value ? '' : t('course-settings.course-details.enter-name', 'Please enter a course name');
                default:
                    return '';
            }
        },
        [t]
    );

    const handleSettingDropDown = ({ key }) => {
        switch (key) {
            case 'delete': {
                return openDeleteCourseDialog(course);
            }
            default:
                return;
        }
    };

    const { errors, validate, validateAll } = useFormValidation(form, getErrorMessage);

    const handleChange = (key, value) => {
        setForm((current) => ({ ...current, [key]: value }));
        setIsDirty(true);
        if (hasClickedSubmit || errors[key]) {
            validate(key, value);
        }
    };

    const deleteCourse = async () => {
        await courseService.deleteCourse(auth.course_id, { course_id: auth.course_id });
        history.push('/');
        return message.success(
            t('message.delete-course-success', '{{courseName}} was deleted successfully.', {
                courseName: _.capitalize(course.name || 'The new course'),
            })
        );
    };

    const openDeleteCourseDialog = (course) => {
        setDialog((current) => ({
            ...current,
            open: true,
            message: t('dialog.delete-course-success.title', 'Delete {{courseName}}?', {
                courseName: course.name || 'this course',
            }),
            description: t(
                'dialog.delete-course-success.description',
                'This course will be deleted permanently from our system, and users will lose access immediately. Please note that this action cannot be undone.'
            ),
            variant: 'destructive',
            actionButtonText: t('controls.delete', 'Delete'),
            cancelButtonText: t('controls.cancel', 'Cancel'),
            onAction: () => {
                closeDialog();
                return deleteCourse();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        }));
    };

    const updateCourseSettings = async (form) => {
        setIsLoading(true);
        try {
            const updatedCourse = {
                name: form.name,
                language: form.language?.value,
            };
            await updateCourse(updatedCourse);
            message.success(t('message.update-course-details-success', 'Course details updated successfully.'));
        } catch (e) {
            console.error(e);
            message.error(t('message.something-went-wrong', 'Something went wrong.'));
        } finally {
            setIsLoading(false);
        }
    };

    const submit = async () => {
        if (!hasClickedSubmit) setHasClickedSubmit(true);
        if (!validateAll()) return;
        await updateCourseSettings({
            name: form.name,
            language: form.language,
        });
        setIsDirty(false);
    };

    const reset = () => {
        setForm({
            name: course?.name || '',
            language: course?.language ? { value: course?.language, key: course?.language } : null,
        });
        setHasClickedSubmit(false);
        setIsDirty(false);
    };

    useEffect(() => {
        if (course) {
            setForm({
                name: course?.name,
                language: course?.language
                    ? { value: course?.language, key: course?.language }
                    : { value: 'English', key: 'English' },
            });
        }
    }, [course]);

    useEffect(() => {
        if (!isDirty) validateAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, isDirty]);

    const props = {
        loading: isLoading,
        form,
        isDirty,
        errors,
        onChange: handleChange,
        onSubmit: submit,
        onCancel: reset,
        handleSettingDropDown,
    };

    const renderResponsiveLayout = (device) => {
        return {
            mobile: (
                <MobileCourseDetails
                    {...props}
                    setSelected={(value) => {
                        if (isDirty) {
                            setAlertDialog((current) => ({
                                ...current,
                                open: true,
                                message: t('dialog.leave-page.title', 'Leave Page?'),
                                description: t(
                                    'dialog.leave-page.description',
                                    'If you leave this page, your changes will be lost.'
                                ),
                                onAction: () => {
                                    setSelected(value);
                                    setAlertDialog((current) => ({ ...current, open: false }));
                                },
                            }));
                        } else {
                            setSelected(value);
                        }
                    }}
                />
            ),
            tablet: <TabletCourseDetails {...props} />,
            desktop: <DesktopCourseDetails {...props} />,
        }[device];
    };
    if (isLoading) return <Loading />;
    return <>{renderResponsiveLayout(device)}</>;
};

export default CourseDetails;
